import { useState, useEffect } from "react";
import { NEWS_ARTICLES } from "./newsData";

const COLORS = {
  navy: "#003366",
  navyDark: "#001f44",
  red: "#cc0000",
  greyPage: "#f0f2f5",
  greyCard: "#e5e8ed",
  greyMid: "#c8cdd6",
  greyDark: "#555e6d",
  white: "#ffffff",
  textDark: "#1a2233",
  textMid: "#3d4a5c",
};

const LOGO =
  "https://arcuchicnc.com/wp-content/uploads/2023/07/Untitled-6.png";

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return w;
}

function NewsNavbar() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <nav
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        background: COLORS.navyDark,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: isMobile ? "0 20px" : "0 40px",
        height: 64,
        boxShadow: "0 2px 16px rgba(0,0,0,0.3)",
      }}
    >
      <a href="#/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
        <img src={LOGO} alt="Arcuchi" style={{ height: 36, objectFit: "contain" }} />
      </a>

      {isMobile ? (
        <>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              display: "flex", flexDirection: "column", gap: 5, padding: 8,
            }}
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{
                  display: "block", width: 24, height: 2,
                  background: "#fff", borderRadius: 2,
                  transition: "0.3s",
                  transform:
                    menuOpen && i === 0 ? "rotate(45deg) translate(5px,5px)" :
                    menuOpen && i === 1 ? "scaleX(0)" :
                    menuOpen && i === 2 ? "rotate(-45deg) translate(5px,-5px)" : "none",
                }}
              />
            ))}
          </button>
          {menuOpen && (
            <div style={{
              position: "absolute", top: 64, left: 0, right: 0,
              background: COLORS.navyDark, borderTop: `2px solid ${COLORS.red}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.3)", zIndex: 999,
            }}>
              {[
                { label: "Home", href: "#/" },
                { label: "Solutions", href: "#solutions" },
                { label: "About", href: "#/about" },
                { label: "News", href: "#/news" },
                { label: "Contact", href: "#/contact" },
              ].map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    display: "block", padding: "14px 24px",
                    fontFamily: "Montserrat,sans-serif", fontSize: 12, fontWeight: 700,
                    color: label === "News" ? "#fff" : "rgba(255,255,255,0.65)",
                    textDecoration: "none", textTransform: "uppercase", letterSpacing: "0.1em",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    borderLeft: label === "News" ? `3px solid ${COLORS.red}` : "3px solid transparent",
                  }}
                >
                  {label}
                </a>
              ))}
            </div>
          )}
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
          {[
            { label: "Home", href: "#/" },
            { label: "Solutions", href: "#solutions" },
            { label: "About", href: "#/about" },
            { label: "News", href: "#/news" },
            { label: "Contact", href: "#/contact" },
          ].map(({ label, href }) => (
            <a
              key={label}
              href={href}
              style={{
                fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
                letterSpacing: "0.1em",
                color: label === "News" ? "#fff" : "rgba(255,255,255,0.65)",
                textDecoration: "none", textTransform: "uppercase", paddingBottom: 2,
                borderBottom: label === "News" ? `2px solid ${COLORS.red}` : "2px solid transparent",
              }}
            >
              {label}
            </a>
          ))}
          <a
            href="#/contact"
            style={{
              background: COLORS.red, color: "#fff",
              fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 700,
              letterSpacing: "0.1em", textTransform: "uppercase",
              padding: "9px 20px", borderRadius: 2, textDecoration: "none",
            }}
          >
            Get Quote
          </a>
        </div>
      )}
    </nav>
  );
}

function NewsHero() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${COLORS.navyDark} 0%, ${COLORS.navy} 100%)`,
        padding: isMobile ? "48px 20px" : "72px 40px",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
        backgroundImage: "radial-gradient(circle at 20% 50%, rgba(204,0,0,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0,64,128,0.3) 0%, transparent 50%)",
        pointerEvents: "none",
      }} />
      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 16 }}>
          <div style={{ width: 32, height: 2, background: COLORS.red }} />
          <span style={{
            fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 700,
            color: COLORS.red, letterSpacing: "0.25em", textTransform: "uppercase",
          }}>
            News & Insights
          </span>
          <div style={{ width: 32, height: 2, background: COLORS.red }} />
        </div>
        <h1 style={{
          fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 28 : 44,
          fontWeight: 900, color: "#fff", textTransform: "uppercase",
          letterSpacing: "0.04em", margin: "0 0 16px",
        }}>
          Latest <span style={{ color: COLORS.red }}>Updates</span>
        </h1>
        <p style={{
          fontFamily: "Georgia,serif", fontSize: isMobile ? 14 : 16,
          color: "rgba(255,255,255,0.65)", lineHeight: 1.7,
          maxWidth: 560, margin: "0 auto",
        }}>
          Industry insights, product announcements, and technical guides from the Arcuchi engineering team.
        </p>
        {/* Breadcrumb */}
        <div style={{ marginTop: 24, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
          <a href="#/" style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)", textDecoration: "none" }}>Home</a>
          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: 10 }}>›</span>
          <span style={{ fontFamily: "Montserrat,sans-serif", fontSize: 11, color: "#fff" }}>News & Events</span>
        </div>
      </div>
    </div>
  );
}

const TAG_COLORS = {
  Technology: "#0055aa",
  "Control Systems": "#006644",
  Hydraulics: "#663300",
  "Upgrade Guide": "#5500aa",
  "Energy Efficiency": "#007755",
  Comparison: "#aa5500",
};

function ArticleCard({ article, featured }) {
  const [hovered, setHovered] = useState(false);
  const w = useWindowWidth();
  const isMobile = w < 768;

  const tagColor = TAG_COLORS[article.tag] || COLORS.navy;

  if (featured && !isMobile) {
    return (
      <a
        href={`#/news/${article.slug}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          overflow: "hidden", borderRadius: 6,
          boxShadow: hovered ? "0 24px 56px rgba(0,0,0,0.2)" : "0 6px 20px rgba(0,0,0,0.1)",
          transform: hovered ? "translateY(-4px)" : "none",
          transition: "transform 0.3s, box-shadow 0.3s",
          textDecoration: "none", background: "#fff",
          gridColumn: "1 / -1",
        }}
      >
        <div style={{ position: "relative", overflow: "hidden", minHeight: 340 }}>
          <img
            src={article.img}
            alt={article.title}
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover",
              transform: hovered ? "scale(1.05)" : "scale(1)", transition: "transform 0.5s",
            }}
          />
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(to right, transparent 60%, rgba(0,15,40,0.15) 100%)",
          }} />
          <span style={{
            position: "absolute", top: 20, left: 20,
            background: tagColor, color: "#fff",
            fontFamily: "Montserrat,sans-serif", fontSize: 9, fontWeight: 800,
            letterSpacing: "0.15em", textTransform: "uppercase", padding: "5px 12px", borderRadius: 2,
          }}>{article.tag}</span>
        </div>
        <div style={{ padding: "40px 36px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 10, color: COLORS.greyDark, letterSpacing: "0.08em", marginBottom: 14 }}>
            {article.date} · {article.readTime}
          </div>
          <h2 style={{
            fontFamily: "Montserrat,sans-serif", fontSize: 22, fontWeight: 800,
            color: COLORS.textDark, textTransform: "uppercase", letterSpacing: "0.03em",
            lineHeight: 1.3, margin: "0 0 16px",
          }}>{article.title}</h2>
          <p style={{ fontFamily: "Georgia,serif", fontSize: 14, color: COLORS.textMid, lineHeight: 1.75, margin: "0 0 24px" }}>
            {article.excerpt}
          </p>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontFamily: "Montserrat,sans-serif", fontSize: 11, fontWeight: 800,
            color: COLORS.red, textTransform: "uppercase", letterSpacing: "0.12em",
          }}>
            Read Article
            <span style={{ transform: hovered ? "translateX(6px)" : "none", transition: "transform 0.25s", display: "inline-block" }}>→</span>
          </div>
        </div>
      </a>
    );
  }

  return (
    <a
      href={`#/news/${article.slug}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", flexDirection: "column",
        overflow: "hidden", borderRadius: 6,
        boxShadow: hovered ? "0 16px 40px rgba(0,0,0,0.18)" : "0 4px 14px rgba(0,0,0,0.09)",
        transform: hovered ? "translateY(-5px)" : "none",
        transition: "transform 0.3s, box-shadow 0.3s",
        textDecoration: "none", background: "#fff",
      }}
    >
      <div style={{ position: "relative", overflow: "hidden", height: isMobile ? 170 : 200 }}>
        <img
          src={article.img}
          alt={article.title}
          style={{
            width: "100%", height: "100%", objectFit: "cover",
            transform: hovered ? "scale(1.06)" : "scale(1)", transition: "transform 0.5s",
          }}
        />
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to top, rgba(0,15,40,0.55) 0%, transparent 55%)",
        }} />
        <span style={{
          position: "absolute", top: 14, left: 14,
          background: tagColor, color: "#fff",
          fontFamily: "Montserrat,sans-serif", fontSize: 8.5, fontWeight: 800,
          letterSpacing: "0.14em", textTransform: "uppercase", padding: "4px 10px", borderRadius: 2,
        }}>{article.tag}</span>
      </div>
      <div style={{ padding: "20px 22px 24px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 9.5, color: COLORS.greyDark, letterSpacing: "0.07em", marginBottom: 10 }}>
          {article.date} · {article.readTime}
        </div>
        <h3 style={{
          fontFamily: "Montserrat,sans-serif", fontSize: 13.5, fontWeight: 800,
          color: COLORS.textDark, textTransform: "uppercase", letterSpacing: "0.025em",
          lineHeight: 1.4, margin: "0 0 10px", flex: 1,
        }}>{article.title}</h3>
        <p style={{ fontFamily: "Georgia,serif", fontSize: 12.5, color: COLORS.textMid, lineHeight: 1.7, margin: "0 0 16px" }}>
          {article.excerpt.length > 100 ? article.excerpt.slice(0, 100) + "..." : article.excerpt}
        </p>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 800,
          color: COLORS.red, textTransform: "uppercase", letterSpacing: "0.1em",
        }}>
          Read More
          <span style={{ transform: hovered ? "translateX(4px)" : "none", transition: "transform 0.25s", display: "inline-block" }}>→</span>
        </div>
      </div>
    </a>
  );
}

function TagFilter({ tags, active, onSelect }) {
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <div style={{
      display: "flex", gap: 8, marginBottom: 32,
      overflowX: isMobile ? "auto" : "visible",
      flexWrap: isMobile ? "nowrap" : "wrap",
      paddingBottom: isMobile ? 4 : 0,
      WebkitOverflowScrolling: "touch",
    }}>
      {["All", ...tags].map((tag) => (
        <button
          key={tag}
          onClick={() => onSelect(tag)}
          style={{
            fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 9.5 : 10, fontWeight: 700,
            letterSpacing: "0.08em", textTransform: "uppercase",
            padding: isMobile ? "8px 14px" : "7px 16px", borderRadius: 2, cursor: "pointer",
            border: `1px solid ${active === tag ? COLORS.red : COLORS.greyMid}`,
            background: active === tag ? COLORS.red : "transparent",
            color: active === tag ? "#fff" : COLORS.greyDark,
            transition: "all 0.2s", whiteSpace: "nowrap", flexShrink: 0,
          }}
        >
          {tag}
        </button>
      ))}
    </div>
  );
}

function NewsFooter() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  return (
    <footer style={{ background: COLORS.navyDark, padding: isMobile ? "36px 20px" : "48px 40px" }}>
      <div style={{
        display: "flex", flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "flex-start" : "center",
        justifyContent: "space-between", gap: 20,
        borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 28, marginBottom: 24,
      }}>
        <img src={LOGO} alt="Arcuchi" style={{ height: 32, objectFit: "contain" }} />
        <div style={{ display: "flex", gap: isMobile ? 16 : 28, flexWrap: "wrap" }}>
          {[
            { label: "Home", href: "#/" },
            { label: "About", href: "#/about" },
            { label: "News", href: "#/news" },
            { label: "Contact", href: "#/contact" },
          ].map(({ label, href }) => (
            <a key={label} href={href} style={{
              fontFamily: "Montserrat,sans-serif", fontSize: 10, fontWeight: 600,
              color: "rgba(255,255,255,0.55)", textDecoration: "none", letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}>{label}</a>
          ))}
        </div>
      </div>
      <p style={{
        fontFamily: "Montserrat,sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)",
        textAlign: "center", margin: 0, letterSpacing: "0.06em",
      }}>
        © 2025 Shenzhen Arcuchi Technology Co., Ltd. All rights reserved.
      </p>
    </footer>
  );
}

export default function NewsPage() {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const isTablet = w < 1024;

  const allTags = [...new Set(NEWS_ARTICLES.map((a) => a.tag))];
  const [activeTag, setActiveTag] = useState("All");

  const filtered =
    activeTag === "All"
      ? NEWS_ARTICLES
      : NEWS_ARTICLES.filter((a) => a.tag === activeTag);

  useEffect(() => {
    document.title = "News & Events — Arcuchi CNC";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ fontFamily: "Montserrat,sans-serif", background: COLORS.greyPage, minHeight: "100vh" }}>
      <NewsNavbar />
      <NewsHero />

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "40px 20px" : "60px 40px" }}>
        <TagFilter tags={allTags} active={activeTag} onSelect={setActiveTag} />

        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "1fr 1fr 1fr",
          gap: 24,
        }}>
          {filtered.map((article, i) => (
            <ArticleCard
              key={article.slug}
              article={article}
              featured={i === 0 && activeTag === "All"}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: COLORS.greyDark }}>
            <p style={{ fontFamily: "Montserrat,sans-serif", fontSize: 14 }}>No articles found for this category.</p>
          </div>
        )}
      </div>

      <NewsFooter />
    </div>
  );
}
